// Settings ▸ Connections: read-only Google Ads / Play Console connectors. Credentials never go through the panel:
// they live on the server as a GROWTH_GOOGLE_* environment variable and the connection only stores its name.

import type { Connection, ConnectionTest, Game, JobRunResult } from '@growth/client';
import { type FormEvent, useState } from 'react';
import { Card, DataTable, Empty, ErrorBox, Icon, JsonView, StatusBadge } from '../components/ui.tsx';
import { fmtAgo, fmtDay } from '../format.ts';
import { invalidateCache, useAction, useApi, useSession } from '../session.tsx';

const PROVIDER: Record<string, string> = { google_ads: 'Google Ads', google_play: 'Google Play Console' };

function TestResult({ r }: { r: ConnectionTest }) {
  if (!r.ok) {
    return (
      <div className="error-box stack" role="alert">
        <span className="row">
          <Icon name="critical" /> {r.error}
        </span>
        {r.hint && <span>{r.hint}</span>}
      </div>
    );
  }
  const d = r.details;
  return (
    <div className="notice stack">
      <span className="row">
        <Icon name="good" /> {r.identity} puede leer {PROVIDER[r.provider]}.
      </span>
      {r.provider === 'google_ads' ? (
        <span>
          Cuenta «{d.account_name ?? '—'}» · {d.currency} · {d.time_zone}
          {d.time_zone_matches_game === false ? ' (distinta de la zona del juego: los días se desplazan)' : ''} · {d.campaigns_for_game} de {d.campaigns_in_account} campañas son de este
          juego{d.campaigns?.length ? `: ${d.campaigns.map((c: { name: string }) => c.name).join(', ')}` : ''}.
        </span>
      ) : (
        <span>
          {d.report_files} informes ({d.installs_files} de instalaciones, {d.store_performance_files} de ficha) · meses {d.first_month ?? '—'} – {d.latest_month ?? '—'}.
        </span>
      )}
    </div>
  );
}

function ConnectionCard({ gameId, c, onChange }: { gameId: string; c: Connection; onChange: () => void }) {
  const { api, isAdmin } = useSession();
  const action = useAction();
  const [test, setTest] = useState<ConnectionTest | null>(null);
  const [sync, setSync] = useState<JobRunResult | null>(null);
  const [details, setDetails] = useState(false);
  const r = c.last_result ?? {};
  const status = c.status === 'error' ? 'error' : c.status === 'ok' ? 'ok' : 'info';
  return (
    <Card
      title={`${PROVIDER[c.provider] ?? c.provider} · ${c.label}`}
      subtitle={
        <>
          <StatusBadge status={c.enabled ? status : 'info'} /> {c.enabled ? '' : 'desactivada · '}
          última sincronización correcta {fmtAgo(c.last_success_at)}
          {c.synced_from ? ` · datos ${fmtDay(c.synced_from)} – ${fmtDay(c.synced_to)}` : ''}
        </>
      }
      actions={
        isAdmin && (
          <>
            <button type="button" className="btn small" disabled={action.busy} onClick={async () => setTest((await action.run(() => api.testConnection(gameId, c.id))) ?? null)}>
              Probar
            </button>
            <button
              type="button"
              className="btn small"
              disabled={action.busy}
              onClick={async () => {
                const res = await action.run(() => api.syncConnection(gameId, c.id));
                if (res) {
                  setSync(res);
                  invalidateCache('ua');
                  invalidateCache('creatives');
                  onChange();
                }
              }}
            >
              {action.busy ? 'Trabajando…' : 'Sincronizar ahora'}
            </button>
            <button
              type="button"
              className="btn small ghost"
              disabled={action.busy}
              onClick={async () => {
                if (await action.run(() => api.updateConnection(gameId, c.id, { enabled: !c.enabled }))) onChange();
              }}
            >
              {c.enabled ? 'Desactivar' : 'Activar'}
            </button>
            <button
              type="button"
              className="btn small danger"
              disabled={action.busy}
              onClick={async () => {
                if (window.confirm('Eliminar la conexión? Los datos ya importados se conservan.') && (await action.run(() => api.deleteConnection(gameId, c.id)))) onChange();
              }}
            >
              Eliminar
            </button>
          </>
        )
      }
    >
      <div className="stack">
        {c.label === 'demo' && <div className="notice">Conexión de demostración: datos sintéticos generados por «pnpm demo:ua» (no viene de Google).</div>}
        <dl className="kv">
          <dt>Credenciales</dt>
          <dd>
            Variable <code>{c.credentials_env}</code> del servidor:{' '}
            {c.credentials_present ? (c.credentials_identity ? <strong>{c.credentials_identity}</strong> : <span>ilegible ({c.credentials_error})</span>) : <strong>no definida</strong>}
          </dd>
          {c.provider === 'google_ads' ? (
            <>
              <dt>Cuenta</dt>
              <dd>
                {String(c.config.customer_id ?? '')}
                {c.config.login_customer_id ? ` (vía MCC ${String(c.config.login_customer_id)})` : ''} · app {String(c.config.app_id ?? 'todas las campañas')}
              </dd>
              <dt>Instalaciones</dt>
              <dd>
                {r.install_source === 'none'
                  ? 'sin acción de conversión de instalación: CPI desconocido'
                  : r.install_actions?.length
                    ? `${(r.install_actions as { name: string; type: string }[]).map((a) => `${a.name} (${a.type})`).join(', ')}${r.install_source === 'config' ? ' · configuradas' : ' · detectadas'}`
                    : '—'}
              </dd>
            </>
          ) : (
            <>
              <dt>Bucket</dt>
              <dd>
                gs://{String(c.config.bucket ?? '')} · {String(c.config.package_name ?? '')}
              </dd>
            </>
          )}
          {c.last_error && (
            <>
              <dt>Último error</dt>
              <dd>{c.last_error}</dd>
            </>
          )}
        </dl>
        {Array.isArray(r.warnings) && r.warnings.length > 0 && (
          <div className="notice stack small">
            {(r.warnings as string[]).map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>
        )}
        {test && <TestResult r={test} />}
        {sync && (
          <div className={sync.status === 'failed' ? 'error-box' : 'notice'}>
            {sync.status === 'succeeded' ? 'Sincronizado.' : sync.status === 'skipped' ? 'Ya había una sincronización en marcha.' : `Falló: ${sync.error}`}
          </div>
        )}
        {action.error && <ErrorBox error={action.error} />}
        <button type="button" className="btn small ghost" style={{ alignSelf: 'flex-start' }} onClick={() => setDetails((v) => !v)} aria-expanded={details}>
          {details ? 'Ocultar detalle' : 'Ver último resultado'}
        </button>
        {details && <JsonView value={{ config: c.config, last_result: c.last_result }} />}
      </div>
    </Card>
  );
}

function AddConnection({ game, onDone }: { game: Game; onDone: () => void }) {
  const { api } = useSession();
  const action = useAction();
  const [provider, setProvider] = useState<'google_ads' | 'google_play'>('google_ads');
  const [f, setF] = useState({ customer_id: '', login_customer_id: '', app_id: game.store_ids.android ?? '', bucket: '', package_name: game.store_ids.android ?? '', label: 'default', credentials_env: 'GROWTH_GOOGLE_SA_JSON' });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const config =
      provider === 'google_ads'
        ? { customer_id: f.customer_id, ...(f.login_customer_id ? { login_customer_id: f.login_customer_id } : {}), ...(f.app_id ? { app_id: f.app_id } : {}) }
        : { bucket: f.bucket, ...(f.package_name ? { package_name: f.package_name } : {}) };
    if (await action.run(() => api.createConnection(game.id, { provider, label: f.label, credentials_env: f.credentials_env, config }))) onDone();
  };
  return (
    <Card
      title="Añadir conexión"
      subtitle="Solo lectura. Las credenciales NO se escriben aquí: el JSON de la cuenta de servicio va en el servidor (infra/.env) en la variable indicada; reinicia api y worker tras cambiarla."
    >
      <form className="stack" onSubmit={submit}>
        <div className="segmented" role="group" aria-label="Proveedor">
          <button type="button" aria-pressed={provider === 'google_ads'} onClick={() => setProvider('google_ads')}>
            Google Ads
          </button>
          <button type="button" aria-pressed={provider === 'google_play'} onClick={() => setProvider('google_play')}>
            Google Play Console
          </button>
        </div>
        <div className="form-grid">
          {provider === 'google_ads' ? (
            <>
              <label className="field">
                <span>Id de cliente (arriba a la derecha en Google Ads)</span>
                <input className="input" required value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} placeholder="123-456-7890" />
              </label>
              <label className="field">
                <span>Cuenta de administrador (MCC), si el acceso es a través de ella</span>
                <input className="input" value={f.login_customer_id} onChange={(e) => setF({ ...f, login_customer_id: e.target.value })} placeholder="opcional" />
              </label>
              <label className="field">
                <span>Paquete de la app (campañas de este juego)</span>
                <input className="input" value={f.app_id} onChange={(e) => setF({ ...f, app_id: e.target.value })} placeholder="com.hiveinteractive.antpocalypse" />
              </label>
            </>
          ) : (
            <>
              <label className="field">
                <span>URI de Cloud Storage (Descargar informes ▸ Estadísticas)</span>
                <input className="input" required value={f.bucket} onChange={(e) => setF({ ...f, bucket: e.target.value })} placeholder="gs://pubsite_prod_rev_01234567890987654321/stats/installs/" />
              </label>
              <label className="field">
                <span>Paquete de la app</span>
                <input className="input" required value={f.package_name} onChange={(e) => setF({ ...f, package_name: e.target.value })} placeholder="com.hiveinteractive.antpocalypse" />
              </label>
            </>
          )}
          <label className="field">
            <span>Etiqueta</span>
            <input className="input" required pattern="[a-z0-9][a-z0-9_\-]{0,39}" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
          </label>
          <label className="field">
            <span>Variable de credenciales en el servidor</span>
            <input className="input" required pattern="GROWTH_GOOGLE_[A-Z0-9_]{1,60}" value={f.credentials_env} onChange={(e) => setF({ ...f, credentials_env: e.target.value.toUpperCase() })} />
          </label>
        </div>
        {action.error && <ErrorBox error={action.error} />}
        <div className="row">
          <button type="submit" className="btn primary" disabled={action.busy}>
            Añadir y probar después
          </button>
        </div>
      </form>
    </Card>
  );
}

export function ConnectionsTab({ game }: { game: Game }) {
  const { isAdmin } = useSession();
  const list = useApi<Connection[]>(`connections:${game.id}`, (api) => api.connections(game.id));
  const reload = () => {
    invalidateCache(`connections:${game.id}`);
    list.reload();
  };
  return (
    <div className="stack">
      <div className="notice">
        Las conexiones solo leen datos (gasto, campañas, instalaciones, ficha de Play). Nada se crea, pausa ni cambia en tus cuentas y nunca se gasta dinero automáticamente. Guía paso a paso:
        EXTERNAL_INTEGRATIONS.md y docs/acquisition.md.
      </div>
      {list.error && <ErrorBox error={list.error} onRetry={list.reload} />}
      {list.data && !list.data.length && <Empty>Sin conexiones todavía.</Empty>}
      {list.data?.map((c) => <ConnectionCard key={c.id} gameId={game.id} c={c} onChange={reload} />)}
      {isAdmin && <AddConnection game={game} onDone={reload} />}
      {list.data && list.data.length > 0 && (
        <Card title="Programación">
          <DataTable
            compact
            data={{
              columns: [{ label: 'Job' }, { label: 'Cuándo' }],
              rows: [
                ['sync-google-ads', 'cada ACQUISITION_SYNC_INTERVAL_MINUTES (6 h por defecto), reimportando los últimos 14 días'],
                ['sync-google-play', 'cada 6 h; solo descarga los ficheros que Google ha cambiado'],
                ['sync-fx', 'una vez al día (tipos de referencia del BCE)'],
              ],
            }}
          />
        </Card>
      )}
    </div>
  );
}
